import {
  ApartmentOutlined,
  CloudSyncOutlined,
  ExpandOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  App,
  Button,
  Empty,
  Form,
  Input,
  Popconfirm,
  Space,
  Spin,
  Switch,
  Tag,
  Tree,
  type TreeProps,
  Typography,
} from 'antd';
import type { DataNode } from 'antd/es/tree';
import { Building2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { http } from '../../api/client.js';
import { FancyModal, PageHeader } from '../../components/ui/index.js';
import { tx } from '../../i18n/translate.js';
import { DingTalkOrgSyncDrawer } from './DingTalkOrgSyncDrawer.js';

interface BuUser {
  key: string;
  fullName: string;
  email: string | null;
  domainName: string | null;
  dingtalkUserId: string | null;
}

interface BuNode {
  key: string;
  title: string;
  parentId: string | null;
  userCount: number;
  totalUserCount: number;
  subtreeUserCount?: number;
  subtreeTotalUserCount?: number;
  divisionName: string | null;
  isDisabled: boolean | null;
  users?: BuUser[];
  children: BuNode[];
}

interface BuFormValues {
  name: string;
  divisionName?: string | null;
  emailAddress?: string | null;
  costCenter?: string | null;
}

function activeDepartmentCount(nodes: BuNode[]): number {
  return nodes.reduce(
    (sum, node) => sum + (node.isDisabled ? 0 : 1) + activeDepartmentCount(node.children),
    0,
  );
}

function disabledDepartmentCount(nodes: BuNode[]): number {
  return nodes.reduce(
    (sum, node) => sum + (node.isDisabled ? 1 : 0) + disabledDepartmentCount(node.children),
    0,
  );
}

function subtreeActiveUsers(node: BuNode): number {
  return (
    node.subtreeUserCount ??
    node.userCount + node.children.reduce((s, c) => s + subtreeActiveUsers(c), 0)
  );
}

function subtreeStoredUsers(node: BuNode): number {
  return (
    node.subtreeTotalUserCount ??
    (node.totalUserCount ?? node.userCount) +
      node.children.reduce((s, c) => s + subtreeStoredUsers(c), 0)
  );
}

function flatten(nodes: BuNode[]): BuNode[] {
  const out: BuNode[] = [];
  for (const n of nodes) {
    out.push(n);
    out.push(...flatten(n.children));
  }
  return out;
}

function findNode(nodes: BuNode[], key: string): BuNode | null {
  for (const n of nodes) {
    if (n.key === key) return n;
    const found = findNode(n.children, key);
    if (found) return found;
  }
  return null;
}

export function buTreeUserNodeKey(businessUnitKey: string, userKey: string) {
  return `user:${businessUnitKey}:${userKey}`;
}

function nodeMatchesQuery(node: BuNode, query: string): boolean {
  const buText = [node.title, node.divisionName].filter(Boolean).join(' ').toLowerCase();
  if (buText.includes(query)) return true;
  return (node.users ?? []).some((user) =>
    [user.fullName, user.email, user.domainName]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(query),
  );
}

export function BuTreePage() {
  const { message } = App.useApp();
  const [nodes, setNodes] = useState<BuNode[]>([]);
  const [uniqueActiveUserCount, setUniqueActiveUserCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [parentForCreate, setParentForCreate] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [purgingDisabled, setPurgingDisabled] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const [syncDrawerOpen, setSyncDrawerOpen] = useState(false);
  const [form] = Form.useForm<BuFormValues>();

  const refresh = useCallback(() => {
    setLoading(true);
    http
      .get<{ nodes: BuNode[]; uniqueActiveUserCount?: number }>(
        '/admin/users/bu-tree?includeUsers=true',
      )
      .then((r) => {
        setNodes(r.data.nodes);
        setUniqueActiveUserCount(r.data.uniqueActiveUserCount ?? 0);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const allKeys = useMemo(() => flatten(nodes).map((n) => n.key), [nodes]);
  const selectedBu = selectedKey ? findNode(nodes, selectedKey) : null;

  const filteredKeys = useMemo(() => {
    if (!q.trim()) return [] as string[];
    const lc = q.trim().toLowerCase();
    const matches = flatten(nodes).filter((n) => nodeMatchesQuery(n, lc));
    const expand = new Set<string>();
    const parentMap = new Map<string, string | null>();
    for (const n of flatten(nodes)) parentMap.set(n.key, n.parentId);
    for (const m of matches) {
      let cur: string | null | undefined = m.key;
      while (cur) {
        expand.add(cur);
        cur = parentMap.get(cur);
      }
    }
    return Array.from(expand);
  }, [q, nodes]);

  const expanded = q.trim() ? filteredKeys : expandedKeys;

  const openCreate = (parentId: string | null) => {
    setParentForCreate(parentId);
    setModalMode('create');
    form.resetFields();
  };
  const openEdit = (bu: BuNode) => {
    setModalMode('edit');
    setSelectedKey(bu.key);
    form.setFieldsValue({
      name: bu.title,
      divisionName: bu.divisionName,
    });
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (modalMode === 'create') {
        await http.post('/admin/bu', {
          name: values.name,
          parentBusinessUnitId: parentForCreate,
          divisionName: values.divisionName ?? null,
          emailAddress: values.emailAddress ?? null,
          costCenter: values.costCenter ?? null,
        });
        message.success(tx('hardcoded:pages.admin.butreepage.aae952bb'));
      } else if (modalMode === 'edit' && selectedKey) {
        await http.patch(`/admin/bu/${selectedKey}`, {
          name: values.name,
          divisionName: values.divisionName ?? null,
          emailAddress: values.emailAddress ?? null,
          costCenter: values.costCenter ?? null,
        });
        message.success(tx('hardcoded:pages.admin.butreepage.6728fc7e'));
      }
      setModalMode(null);
      refresh();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(
        tx('hardcoded:pages.admin.butreepage.e57094c9', {
          v0: err.response?.data?.message ?? tx('hardcoded:pages.admin.butreepage.a28c2e74'),
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (buId: string) => {
    try {
      await http.delete(`/admin/bu/${buId}`);
      message.success(tx('hardcoded:pages.admin.butreepage.d2376308'));
      setSelectedKey(null);
      refresh();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(
        tx('hardcoded:pages.admin.butreepage.27ccad61', {
          v0: err.response?.data?.message ?? tx('hardcoded:pages.admin.butreepage.a28c2e74'),
        }),
      );
    }
  };

  const handlePurgeDisabled = async () => {
    setPurgingDisabled(true);
    try {
      const res = await http.post<{ deleted: number; skipped?: number }>(
        '/admin/bu/purge-disabled',
      );
      const skipped = res.data.skipped ?? 0;
      if (res.data.deleted > 0 && skipped > 0) {
        message.success(
          tx('hardcoded:pages.admin.butreepage.purge_disabled_partial', {
            v0: res.data.deleted,
            v1: skipped,
          }),
        );
      } else if (res.data.deleted > 0) {
        message.success(
          tx('hardcoded:pages.admin.butreepage.purge_disabled_success', { v0: res.data.deleted }),
        );
      } else if (skipped > 0) {
        message.warning(
          tx('hardcoded:pages.admin.butreepage.purge_disabled_skipped', { v0: skipped }),
        );
      } else {
        message.success(tx('hardcoded:pages.admin.butreepage.purge_disabled_none'));
      }
      setSelectedKey(null);
      refresh();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(
        tx('hardcoded:pages.admin.butreepage.purge_disabled_failed', {
          v0: err.response?.data?.message ?? tx('hardcoded:pages.admin.butreepage.a28c2e74'),
        }),
      );
    } finally {
      setPurgingDisabled(false);
    }
  };

  const toAntdTree = (treeNodes: BuNode[]): DataNode[] =>
    treeNodes.map((n) => {
      const subtotal = subtreeActiveUsers(n);
      const storedSubtotal = subtreeStoredUsers(n);
      const directDisabledUsers = Math.max(0, (n.totalUserCount ?? n.userCount) - n.userCount);
      const descendantDisabledUsers = Math.max(0, storedSubtotal - subtotal - directDisabledUsers);
      const activeUserChildren: DataNode[] = showUsers
        ? (n.users ?? []).map((user) => ({
            key: buTreeUserNodeKey(n.key, user.key),
            selectable: false,
            isLeaf: true,
            title: (
              <Space size={6}>
                <UserOutlined style={{ color: '#64748b' }} />
                <span>{user.fullName}</span>
                {(user.email ?? user.domainName) && (
                  <Typography.Text type="secondary">
                    {user.email ?? user.domainName}
                  </Typography.Text>
                )}
                {user.dingtalkUserId && (
                  <Tag color="processing">{tx('legacy:pages.admin.butreepage.dingtalkBound')}</Tag>
                )}
              </Space>
            ),
          }))
        : [];
      const childNodes = [...toAntdTree(n.children), ...activeUserChildren];
      return {
        key: n.key,
        title: (
          <Space>
            <span style={{ color: n.isDisabled ? '#bfbfbf' : '#262626' }}>
              <ApartmentOutlined
                style={{ color: n.isDisabled ? '#bfbfbf' : '#0078d4', marginRight: 4 }}
              />
              {n.title}
            </span>
            {n.divisionName && (
              <Tag color="cyan" style={{ marginLeft: 4 }}>
                {n.divisionName}
              </Tag>
            )}
            {n.isDisabled && (
              <Tag color="default">{tx('hardcoded:pages.admin.butreepage.693aaa67')}</Tag>
            )}
            <Tag color="blue">
              <UserOutlined /> {n.userCount}
            </Tag>
            {directDisabledUsers > 0 && (
              <Tag color="default">
                {tx('legacy:pages.admin.butreepage.disabledUsers', {
                  count: directDisabledUsers,
                })}
              </Tag>
            )}
            {n.children.length > 0 && (
              <Tag color="purple">
                <TeamOutlined /> {tx('hardcoded:pages.admin.butreepage.85f815ae')}
                {subtotal}
              </Tag>
            )}
            {n.children.length > 0 && descendantDisabledUsers > 0 && (
              <Tag color="default">
                {tx('legacy:pages.admin.butreepage.disabledUsers', {
                  count: descendantDisabledUsers,
                })}
              </Tag>
            )}
          </Space>
        ),
        children: childNodes.length > 0 ? childNodes : undefined,
      };
    });

  const treeData = toAntdTree(nodes);
  const activeDepartments = useMemo(() => activeDepartmentCount(nodes), [nodes]);
  const disabledDepartments = useMemo(() => disabledDepartmentCount(nodes), [nodes]);
  const totalUsersAll = useMemo(
    () => uniqueActiveUserCount || nodes.reduce((s, n) => s + subtreeActiveUsers(n), 0),
    [nodes, uniqueActiveUserCount],
  );

  const onExpand: TreeProps['onExpand'] = (keys) => setExpandedKeys(keys as string[]);
  const onSelect: TreeProps['onSelect'] = (keys) => setSelectedKey((keys[0] as string) ?? null);

  return (
    <Spin spinning={loading}>
      <PageHeader
        title={tx('legacy:pages.admin.butreepage.f8d0ec76')}
        icon={<Building2 size={20} />}
        subtitle={
          <span>
            <strong style={{ color: '#1f2937' }}>{activeDepartments}</strong>{' '}
            {tx('hardcoded:pages.admin.butreepage.e774bae3')}{' '}
            <strong style={{ color: '#1f2937' }}>{totalUsersAll}</strong>{' '}
            {tx('hardcoded:pages.admin.butreepage.63747a2a')}
            {selectedBu && (
              <>
                {tx('hardcoded:pages.admin.butreepage.0e87cb03')}
                <strong style={{ color: '#0078d4' }}>{selectedBu.title}</strong>
              </>
            )}
          </span>
        }
        extra={
          <Space>
            <Input
              allowClear
              placeholder={tx('hardcoded:pages.admin.butreepage.4ca71995')}
              prefix={<SearchOutlined />}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ width: 240 }}
            />
            <Button icon={<ReloadOutlined />} onClick={refresh}>
              {tx('hardcoded:pages.admin.butreepage.ebc76f6b')}
            </Button>
            <Space size={6}>
              <Switch checked={showUsers} onChange={setShowUsers} />
              <Typography.Text>{tx('legacy:pages.admin.butreepage.showUsers')}</Typography.Text>
            </Space>
            <Button icon={<CloudSyncOutlined />} onClick={() => setSyncDrawerOpen(true)}>
              {tx('legacy:pages.admin.butreepage.dingtalkSync')}
            </Button>
            <Popconfirm
              title={tx('hardcoded:pages.admin.butreepage.purge_disabled_confirm_title')}
              description={tx('hardcoded:pages.admin.butreepage.purge_disabled_confirm_desc')}
              okText={tx('hardcoded:pages.admin.butreepage.purge_disabled_confirm_ok')}
              okButtonProps={{ danger: true, loading: purgingDisabled }}
              cancelText={tx('hardcoded:pages.admin.butreepage.498dc9fe')}
              onConfirm={handlePurgeDisabled}
            >
              <Button
                danger
                loading={purgingDisabled}
                disabled={disabledDepartments === 0}
                icon={<Trash2 size={14} />}
              >
                {tx('hardcoded:pages.admin.butreepage.purge_disabled')}
              </Button>
            </Popconfirm>
            <Button icon={<ExpandOutlined />} onClick={() => setExpandedKeys(allKeys)}>
              {tx('hardcoded:pages.admin.butreepage.f204a62b')}
            </Button>
            <Button onClick={() => setExpandedKeys([])}>
              {tx('hardcoded:pages.admin.butreepage.dd64fab5')}
            </Button>
            <Button type="primary" icon={<Plus size={14} />} onClick={() => openCreate(null)}>
              {tx('hardcoded:pages.admin.butreepage.f82dfdfb')}
            </Button>
          </Space>
        }
      />

      {selectedBu && (
        <div
          style={{
            padding: '10px 14px',
            marginBottom: 12,
            background: 'rgba(0, 120, 212, 0.06)',
            borderRadius: 10,
            border: '1px solid rgba(0, 120, 212, 0.20)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography.Text strong>
            {tx('hardcoded:pages.admin.butreepage.642ff02a')}
            {selectedBu.title}
          </Typography.Text>
          <Space size={6}>
            <Button
              size="small"
              icon={<Plus size={12} />}
              onClick={() => openCreate(selectedBu.key)}
            >
              {tx('hardcoded:pages.admin.butreepage.6f5aaaf9')}
            </Button>
            <Button size="small" icon={<Pencil size={12} />} onClick={() => openEdit(selectedBu)}>
              {tx('hardcoded:pages.admin.butreepage.a23f572c')}
            </Button>
            <Popconfirm
              title={tx('hardcoded:pages.admin.butreepage.30ff1bc3')}
              description={
                (selectedBu.totalUserCount ?? selectedBu.userCount) > 0 ||
                selectedBu.children.length > 0
                  ? tx('hardcoded:pages.admin.butreepage.5d44cb3f', {
                      v0: selectedBu.totalUserCount ?? selectedBu.userCount,
                      v1: selectedBu.children.length,
                    })
                  : tx('hardcoded:pages.admin.butreepage.57de3c65')
              }
              okText={tx('hardcoded:pages.admin.butreepage.88ea321c')}
              okButtonProps={{
                danger: true,
                disabled:
                  (selectedBu.totalUserCount ?? selectedBu.userCount) > 0 ||
                  selectedBu.children.length > 0,
              }}
              cancelText={tx('hardcoded:pages.admin.butreepage.498dc9fe')}
              onConfirm={() => handleDelete(selectedBu.key)}
            >
              <Button
                size="small"
                danger
                icon={<Trash2 size={12} />}
                disabled={
                  (selectedBu.totalUserCount ?? selectedBu.userCount) > 0 ||
                  selectedBu.children.length > 0
                }
              >
                {tx('hardcoded:pages.admin.butreepage.88ea321c')}
              </Button>
            </Popconfirm>
            <Button size="small" type="text" onClick={() => setSelectedKey(null)}>
              {tx('hardcoded:pages.admin.butreepage.c65bc733')}
            </Button>
          </Space>
        </div>
      )}

      <div className="crest-list-card">
        {nodes.length === 0 ? (
          <Empty description={tx('hardcoded:pages.admin.butreepage.83ff334a')} />
        ) : (
          <Tree
            treeData={treeData}
            expandedKeys={expanded}
            onExpand={onExpand}
            selectedKeys={selectedKey ? [selectedKey] : []}
            onSelect={onSelect}
            showLine
            blockNode
          />
        )}
      </div>

      <FancyModal
        open={modalMode !== null}
        title={
          modalMode === 'create'
            ? tx('hardcoded:pages.admin.butreepage.57e40395')
            : tx('hardcoded:pages.admin.butreepage.2e46e1d7')
        }
        subtitle={
          modalMode === 'create' && parentForCreate
            ? tx('hardcoded:pages.admin.butreepage.37a55e33', {
                v0: findNode(nodes, parentForCreate)?.title ?? '',
              })
            : modalMode === 'create'
              ? tx('hardcoded:pages.admin.butreepage.fc045536')
              : tx('hardcoded:pages.admin.butreepage.d2e8ff50')
        }
        icon={<Building2 size={18} />}
        onCancel={() => setModalMode(null)}
        onOk={handleSubmit}
        confirmLoading={saving}
        okText={
          modalMode === 'create'
            ? tx('hardcoded:pages.admin.butreepage.cc2215e9')
            : tx('hardcoded:pages.admin.butreepage.0358fd8b')
        }
        cancelText={tx('hardcoded:pages.admin.butreepage.498dc9fe')}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={tx('legacy:pages.admin.butreepage.0efeca48')}
            name="name"
            rules={[{ required: true, message: tx('hardcoded:pages.admin.butreepage.782038e5') }]}
          >
            <Input placeholder={tx('hardcoded:pages.admin.butreepage.62383842')} allowClear />
          </Form.Item>
          <Form.Item label={tx('legacy:pages.admin.butreepage.301d2ad8')} name="divisionName">
            <Input placeholder={tx('hardcoded:pages.admin.butreepage.0b8cdb69')} allowClear />
          </Form.Item>
          <Form.Item label={tx('legacy:pages.admin.butreepage.7b4601b8')} name="emailAddress">
            <Input placeholder={tx('hardcoded:pages.admin.butreepage.9898034d')} allowClear />
          </Form.Item>
          <Form.Item label={tx('legacy:pages.admin.butreepage.8e667fe5')} name="costCenter">
            <Input placeholder={tx('hardcoded:pages.admin.butreepage.9e98e306')} allowClear />
          </Form.Item>
        </Form>
      </FancyModal>
      <DingTalkOrgSyncDrawer
        open={syncDrawerOpen}
        onClose={() => setSyncDrawerOpen(false)}
        onSynced={refresh}
      />
    </Spin>
  );
}
